
import { mergeMap, filter, distinct, catchError } from 'rxjs/operators'
import * as isNumber from 'lodash/isNumber'
import * as sumBy from 'lodash/sumBy'
import { Epic } from 'interfaces/middleware'
import { combineEpics } from 'redux-observable'
import { Observable, merge, empty, of, from, concat } from 'rxjs'
import { get, post } from 'helpers/observableAjaxHelpers'
import { createTimeperiod, timeperiodToTimeframe } from 'helpers/dateTimeHelpers'
import { isCollectionEmpty, normalize } from 'helpers/normalizeHelpers'
import { CollectionCategory, MetaData } from 'interfaces/meta'
import { State, Store } from 'interfaces/state'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_CREATE_INCOME_TRACKER, FORM_CREATE_SPENDING_TRACKER } from 'constants/forms'
import {
  BaseTransactionsPayload,
  MoneyflowBreakdownCollection,
  MoneyflowBreakdownItem,
  MoneyflowTracker,
  MoneyflowType,
  RawMoneyflowTracker,
  TransactionsPayload,
  FullMoneyflowType,
  FullMoneyflowTyped,
  PatchTransactionAPIPayload,
} from 'interfaces/moneyflow'
import {
  DELETE_INCOME_TRACKER_URL,
  DELETE_SPENDING_TRACKER_URL,
  GET_INCOME_TRACKERS_URL,
  GET_MEMBER_INCOME_BREAKDOWN_URL,
  GET_MEMBER_INCOME_TREND_URL,
  GET_MEMBER_SPENDING_BREAKDOWN_URL,
  GET_MEMBER_SPENDING_TREND_URL,
  GET_PEER_GROUPS_INCOME_BREAKDOWN_URL,
  GET_PEER_GROUPS_INCOME_TREND_URL,
  GET_PEER_GROUPS_SPENDING_BREAKDOWN_URL,
  GET_PEER_GROUPS_SPENDING_TREND_URL,
  GET_SPENDING_TRACKERS_URL,
  GET_TRANSACTIONS_URL,
  PATCH_TRANSACTION_URL,
} from 'interfaces/ajax'
import { TimeScale } from 'interfaces/ajax/base'
import {
  appendMoneyflowTrackerActionCreator,
  appendPeergroupMoneyflowBreakdownActionCreator,
  appendPeergroupMoneyflowTrendsActionCreator,
  DELETE_MONEYFLOW_TRACKER,
  deleteMoneyflowTrackerActionCreator,
  GET_MEMBER_MONEYFLOW_BREAKDOWN,
  GET_MEMBER_MONEYFLOW_TRENDS,
  GET_MONEYFLOW_TRACKERS,
  GET_PEER_GROUP_MONEYFLOW_BREAKDOWN,
  GET_PEER_GROUP_MONEYFLOW_TRENDS,
  GET_TRANSACTIONS,
  getMoneyflowTrackersActionCreator,
  getTransactionsActionCreator,
  setMemberMoneyflowBreakdownActionCreator,
  setMemberMoneyflowTrendsActionCreator,
  setTransactionActionCreator,
  setTransactionsActionCreator,
  toggleTransactionEditActionCreator,
  toggleTransactionsLoadingActionCreator,
  CHANGE_TRANSACTION_CATEGORY,
  changeTransactionCategoryActionCreator,
} from 'actions/moneyflowActions'
import { getInsightsActionCreator, INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { getMetaCategoriesByType, resolveCategory, saturateCategory } from 'helpers/categoriesHelpers'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { NOT_FOUND_STATUS_CODE } from 'constants/ajax'
import { Action } from 'actions'
import { addNotificationActionCreator } from 'actions/notificationsActions'
import { editingTransactionSelector, metaMoneyflowCategoriesSelector, transactionEditingIdSelector } from 'selectors/moneyflowSelectors'
import { createTrackerNotification } from 'helpers/notificationsHelpers'
import { createMoneyflowBreakdownDiscardTriggers, createMoneyflowTrendDiscardTriggers, PEERGROUPS_DATA_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { getEditTransactionFormName } from 'helpers/formatHelpers'
import { metaSelector } from 'selectors/globalSelectors'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { Timeframe } from 'interfaces/chart'
import { isTimeframeHistorical } from 'helpers/moneyflowHelpers'
import { processError } from 'helpers/ajaxHelpers'
import { GetPaginatedTransactionsApiPayload } from 'interfaces/ajax/payload'
import { extractOrderingApiPayload, extractPaginatableApiPayload } from 'helpers/bountyHelpers'
import { TRANSACTIONS_PAGE } from 'constants/pages'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { getCTACategory } from 'helpers/insightHelpers'
import { saturateTransactions, saturateTransaction } from 'helpers/rawHelpers'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { formSubmitted } from 'rxOperators/formsLegacy'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { catchSafe } from 'rxOperators/catchSafe'
import { accountsFilterQueryParamSelector } from 'components/features/pageFilters/selectors'

function processBreakdown (breakdown: MoneyflowBreakdownItem[], meta: MetaData, type: MoneyflowType, timeframe: Timeframe): MoneyflowBreakdownCollection {
  const allCategoryBreakdown = {
    category: ALL_CATEGORY_ID,
    amount: sumBy(breakdown, 'amount'),
    projected: sumBy(breakdown, 'projected'),
    average: sumBy(breakdown, 'average'),
  }

  const { allKeys: categoryKeys } = getMetaCategoriesByType(type, meta) as CollectionCategory
  const {
    allKeys: breakdownKeys,
    byKey: breakdownByKey,
  } = normalize([allCategoryBreakdown, ...breakdown], 'category')

  categoryKeys.forEach((categoryKey) => {
    if (!breakdownByKey[categoryKey]) {
      breakdownByKey[categoryKey] = {
        category: categoryKey,
        amount: 0,
        average: 0,
      }

      // historical timeframes should not have projected values
      if (!isTimeframeHistorical(timeframe)) {
        breakdownByKey[categoryKey].projected = 0
      }

      breakdownKeys.push(categoryKey)
    }
  })

  return {
    allKeys: breakdownKeys,
    byKey: breakdownByKey,
  }
}

// ===========================================================================//
//                                  BREAKDOWN                                 //
// ===========================================================================//

const createMemberMoneyflowBreakdownEpic = (type: MoneyflowType): Epic => {
  const triggers = createMoneyflowBreakdownDiscardTriggers(type)

  return (action$, store) => {
    const getBreakdown$ = action$
      .pipe(byType(GET_MEMBER_MONEYFLOW_BREAKDOWN)).pipe(
        filter((action) => action.payload.type === type))

    return getBreakdown$.pipe(
      distinct(
        (a) => JSON.stringify(a.payload),
        action$.pipe(toDiscardCache(triggers)),
      ),
      mergeMap(({ payload }) => {
        const state = store.getState()
        const { meta } = state.data
        const { timeframe } = payload

        const loaded = state.data[type].breakdown[timeframe]
        const isCached = !isCollectionEmpty(loaded)
        if (isCached) {
          return empty()
        }

        const requestPayload = {
          ...createTimeperiod(timeframe),
          excluded_account_uids: accountsFilterQueryParamSelector(state),
        }

        const request = type === 'spending' ?
          get(GET_MEMBER_SPENDING_BREAKDOWN_URL, requestPayload) :
          get(GET_MEMBER_INCOME_BREAKDOWN_URL, requestPayload)

        return request
          .pipe(mapSafe(({ response: breakdown }) =>
            setMemberMoneyflowBreakdownActionCreator(
              type,
              timeframe,
              processBreakdown(breakdown, meta, type, timeframe),
            ),
          ))
      }))
  }
}

const createGetPeergroupMoneyflowBreakdownEpic = (type: MoneyflowType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_PEER_GROUP_MONEYFLOW_BREAKDOWN)).pipe(
        filter((a) => a.payload.type === type),
        distinct(
          (a) => JSON.stringify(a.payload),
          action$.pipe(toDiscardCache(PEERGROUPS_DATA_DISCARD_TRIGGERS)),
        ),
        mergeMap(({ payload }) => {
          const state = store.getState()
          const { meta } = state.data
          const { timeframe, peergroups } = payload

          const loaded = state.data.peergroups[type].breakdown[timeframe]

          const requestedGroups = loaded ?
            peergroups.filter((id) => !loaded.byKey[id]) :
            peergroups

          const isCached = requestedGroups.length === 0
          if (isCached) {
            return empty()
          }

          const timeperiod = createTimeperiod(timeframe)
          const requestPayload = {
            ...timeperiod,
            group: requestedGroups,
          }

          const request = type === 'spending'
            ? get(GET_PEER_GROUPS_SPENDING_BREAKDOWN_URL, requestPayload)
            : get(GET_PEER_GROUPS_INCOME_BREAKDOWN_URL, requestPayload)

          return request
            .pipe(mapSafe(({ response: items }) => {
              const normalizedBreakdowns = items.map(({ id, breakdown }) => ({
                id,
                breakdown: processBreakdown(breakdown, meta, type, timeframe),
              }))
              const normalized = normalize(normalizedBreakdowns, 'id')

              return appendPeergroupMoneyflowBreakdownActionCreator(type, timeframe, normalized)
            }))
        }))


// ===========================================================================//
//                                    TRENDS                                  //
// ===========================================================================//

/**
 * For moneyflow pages
 */
const createMemberMoneyflowTrendEpic = (type: MoneyflowType): Epic => {
  const triggers = createMoneyflowTrendDiscardTriggers(type)

  return (action$, store) => {
    const getTrends$ = action$
      .pipe(byType(GET_MEMBER_MONEYFLOW_TRENDS)).pipe(
        filter((action) => action.payload.type === type))

    return getTrends$.pipe(
      distinct(
        (a) => JSON.stringify(a.payload),
        action$.pipe(toDiscardCache(triggers)),
      ),
      mergeMap(({ payload: { timeframe, category }}) => {
        const state = store.getState()

        const loaded = state.data[type].trends[timeframe][category]
        const isCached = Boolean(loaded)
        if (isCached) {
          return empty()
        }

        const timeperiod = createTimeperiod(timeframe)
        const scale: TimeScale = timeframe === 'current-month' || timeframe === 'last-month' ? 'day' : 'month'
        const requestPayload = {
          category: resolveCategory(category, true),
          scale,
          excluded_account_uids: accountsFilterQueryParamSelector(state),
          ...timeperiod,
        }

        const request = type === 'spending' ?
          get(GET_MEMBER_SPENDING_TREND_URL, requestPayload) :
          get(GET_MEMBER_INCOME_TREND_URL, requestPayload)

        return request
          .pipe(mapSafe(({ response: trend }) =>
            setMemberMoneyflowTrendsActionCreator(type, timeframe, category, trend),
          ))
      }))
  }
}

const createGetPeergroupMoneyflowTrendEpic = (type: MoneyflowType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_PEER_GROUP_MONEYFLOW_TRENDS)).pipe(
        filter((a) => a.payload.type === type),
        distinct(
          (a) => JSON.stringify(a.payload),
          action$.pipe(toDiscardCache(PEERGROUPS_DATA_DISCARD_TRIGGERS)),
        ),
        mergeMap(({ payload }) => {
          const state = store.getState()
          const { timeframe, category, peergroups } = payload

          const loaded = state.data.peergroups[type].trends[timeframe][category]

          const requestedGroups = loaded ?
            peergroups.filter((id) => !loaded.byKey[id]) :
            peergroups

          const isCached = requestedGroups.length === 0
          if (isCached) {
            return empty()
          }

          const timeperiod = createTimeperiod(timeframe)
          const requestPayload = {
            ...timeperiod,
            group: requestedGroups,
            category: resolveCategory(category, true),
          }

          const request = type === 'spending'
            ? get(GET_PEER_GROUPS_SPENDING_TREND_URL, requestPayload)
            : get(GET_PEER_GROUPS_INCOME_TREND_URL, requestPayload)

          return request
            .pipe(mapSafe(({ response: trends }) => {
              const trendsCollection = normalize(trends, 'id')
              return appendPeergroupMoneyflowTrendsActionCreator(type, timeframe, category, trendsCollection)
            }))
        }))

// ===========================================================================//
//                                  TRACKERS                                  //
// ===========================================================================//

const getInsightParams = (state: State, type: MoneyflowType) => {
  const { app, data } = state
  return {
    timeframe: app[type].timeframe,
    peersRelationId: data.peergroups.yourRelationId,
    othersRelationId: app.sidebar.selectedRelationId,
  }
}

const transformTracker = (tracker: RawMoneyflowTracker): MoneyflowTracker => {
  const { category, ...other } = tracker
  return {
    ...other,
    category: category === null ? ALL_CATEGORY_ID : category,
  }
}

const getMoneyflowTrackersEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_MONEYFLOW_TRACKERS, 'request')).pipe(
      distinct((a) => a.payload),
      mergeMap(({ payload }) => {
        const { type } = payload.params
        const request = type === 'spending' ? get(GET_SPENDING_TRACKERS_URL) : get(GET_INCOME_TRACKERS_URL)

        return request
          .pipe(mapSafe(({ response: trackers }) => {
            const transformedTrackers = trackers.map(transformTracker)

            return getMoneyflowTrackersActionCreator.success(payload.params, normalize(transformedTrackers, 'category'))
          }))
      }))

const createTrackerFormEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(FORM_SUBMITTED_SUCCESSFULLY)).pipe(
      mergeMap(({ payload }) => {
        if (payload.formName === FORM_CREATE_SPENDING_TRACKER || payload.formName === FORM_CREATE_INCOME_TRACKER) {
          const type = payload.formName === FORM_CREATE_SPENDING_TRACKER ? 'spending' : 'income'
          const { timeframe, peersRelationId, othersRelationId } = getInsightParams(store.getState(), type)
          const categoryId = resolveCategory(payload.response.category, false)
          const isNew = payload.formValues.isNew
          const state = store.getState()
          const categories = metaMoneyflowCategoriesSelector(state, { type })
          if (!categories) {
            throw new Error(`Cannot handle ${ FORM_SUBMITTED_SUCCESSFULLY } of form ${ payload.formName } without categories`)
          }
          const category = categories.byKey[categoryId]
          const notification = createTrackerNotification({ ...payload.response, category, type, isNew })

          const actions: Action[] = [
            addNotificationActionCreator(notification),
            appendMoneyflowTrackerActionCreator(type, { ...payload.response, category: categoryId }),
          ]
          if (isNumber(peersRelationId) && isNumber(othersRelationId)) {
            actions.push(
              getInsightsActionCreator(type, timeframe, peersRelationId, othersRelationId, categoryId),
            )
          }
          if (isMobileSelector(store.getState())) {
            actions.push(
            // TODO: close tracker pane
            )
          }

          return of(...actions)
        }

        return empty()
      }))

const deleteTrackerEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(DELETE_MONEYFLOW_TRACKER, 'request')).pipe(
      mergeMap((a) => {
        const { type, tracker } = a.payload.params
        const params = { id: tracker.id }
        const request = type === 'spending'
          ? post({ url: DELETE_SPENDING_TRACKER_URL, params })
          : post({ url: DELETE_INCOME_TRACKER_URL, params })

        return request
          .pipe(mergeMapSafe(() =>
            from([
            // TODO: close tracker pane
              deleteMoneyflowTrackerActionCreator.success(a.payload.params, {}),
            ]),
          ))
      }))


// ===========================================================================//
//                                TRANSACTIONS                                //
// ===========================================================================//

function constructTransactionApiPayload (state: State, payload: BaseTransactionsPayload): GetPaginatedTransactionsApiPayload {
  const {
    type,
    timeframe: payloadTimeframe,
    category: payloadCategory,
  } = payload

  const {
    timeframe: stateTimeframe,
    categories,
    transactions,
  } = state.app[type]

  const timeframe = payloadTimeframe || stateTimeframe
  const timeperiod = createTimeperiod(timeframe)

  const category = payloadCategory || categories.selectedId

  return {
    ...extractPaginatableApiPayload(transactions, payload),
    ...extractOrderingApiPayload(transactions, payload),
    ...timeperiod,
    category: resolveCategory(category, true),
    ...type !== TRANSACTIONS_PAGE ? { base_type: type } : {},
    excluded_account_uids: accountsFilterQueryParamSelector(state),
  }
}

const requestTransactions = (store: Store, payload: BaseTransactionsPayload) => {
  const state = store.getState()
  const { type } = payload

  const request$ = get(GET_TRANSACTIONS_URL, constructTransactionApiPayload(state, payload)).pipe(
    mergeMap(({ response }) => {
      const { meta } = state.data

      // pagination
      if (!('results' in response)) {
        return empty()
      }

      const setPayload: TransactionsPayload = {
        ...payload,
        transactions: { ...response, results: saturateTransactions(response.results, meta) },
      }

      return from([
        setTransactionsActionCreator(setPayload),
        toggleTransactionsLoadingActionCreator(type, false),
      ])
    }),
    catchError((error): Observable<Action> => {
      if (error.status === NOT_FOUND_STATUS_CODE && payload.page !== 1) {
        // requesting the first page
        return of(
          getTransactionsActionCreator({ ...payload, page: 1 }),
        )
      }

      return processError(error)
    }))

  return concat(
    of(toggleTransactionsLoadingActionCreator(type, true)),
    request$,
  )
}

const getTransactionsEpic = (type: FullMoneyflowType): Epic => {
  return (action$, store) =>
    action$
      .pipe(byType(GET_TRANSACTIONS)).pipe(
        filter((action) => action.payload.type === type),
        mergeMap(({ payload }) => requestTransactions(store, payload)))
}

const changeTransactionCategoryEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(CHANGE_TRANSACTION_CATEGORY, 'request')).pipe(
      mergeMap(({ payload }) => {
        const meta = metaSelector(store.getState())
        const { transaction, category, apply_to_ids, create_rule } = payload.params

        const params = { id: transaction.uid }
        const body: PatchTransactionAPIPayload = {
          transaction: { category: category.id },
          create_rule,
          apply_to_ids,
        }

        const request$ = post({ url: PATCH_TRANSACTION_URL, params }, { body }).pipe(
          mergeMap(({ response }) => {
            const nextTransaction = saturateTransaction(response.transaction, meta)
            if (!nextTransaction) {
              return empty()
            }

            return of(changeTransactionCategoryActionCreator.success(payload.params, nextTransaction))
          }))
          .pipe(catchSafe(() => of(changeTransactionCategoryActionCreator.fail(payload.params, -1))))

        return request$
      }))

const editTransactionSubmittedEpic = (type: FullMoneyflowType): Epic => {
  const formName = getEditTransactionFormName(type)
  const typed: FullMoneyflowTyped = { type }

  return (action$, store) =>
    action$
      .pipe(formSubmitted(formName))
      .pipe(mergeMapSafe(({ payload }) => {
        if (payload.formName !== formName) {
          return empty()
        }

        const state = store.getState()
        const meta = metaSelector(state)

        // update single transaction
        const oldTransaction = editingTransactionSelector(state, typed)
        const newTransaction = oldTransaction
          ? saturateTransaction({
            ...oldTransaction,
            ...payload.response.transaction,
          }, meta)
          : null

        const update$ = newTransaction
          ? of(setTransactionActionCreator(type, newTransaction))
          : empty()

        const editingId = transactionEditingIdSelector(state, typed)
        const closeForm$ = editingId
          ? of(toggleTransactionEditActionCreator(type, editingId, false))
          : empty()


        return merge(
          update$,
          closeForm$,
        )
      }))
}

const ctaSetTrackerEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      mergeMap((action) => {
        if (action.payload.type !== 'edit_tracker' && action.payload.type !== 'set_tracker') {
          return empty()
        }

        const { cta } = action.payload

        const category = getCTACategory(cta.filters)

        if (!category) {
          return empty()
        }
        const state = store.getState()
        const meta = metaSelector(state)
        const categoryInfo = saturateCategory(category, meta)

        const { start_date, end_date } = cta.filters

        const timeframe = timeperiodToTimeframe({
          start_date,
          end_date,
        })

        if (!categoryInfo || !categoryInfo.type) {
          return empty()
        }

        return of(modifyFloatingPaneActionCreator('tracker', {
          payload: {
            category,
            type: categoryInfo.type,
            timeframe,
          },
        }))
      }))

export default combineEpics(
  createMemberMoneyflowBreakdownEpic('spending'),
  createMemberMoneyflowBreakdownEpic('income'),
  createGetPeergroupMoneyflowBreakdownEpic('spending'),
  createGetPeergroupMoneyflowBreakdownEpic('income'),
  createMemberMoneyflowTrendEpic('spending'),
  createMemberMoneyflowTrendEpic('income'),
  createGetPeergroupMoneyflowTrendEpic('spending'),
  createGetPeergroupMoneyflowTrendEpic('income'),
  getTransactionsEpic('spending'),
  getTransactionsEpic('income'),
  getTransactionsEpic('transactions'),
  editTransactionSubmittedEpic('spending'),
  editTransactionSubmittedEpic('income'),
  editTransactionSubmittedEpic('transactions'),
  changeTransactionCategoryEpic,
  getMoneyflowTrackersEpic,
  deleteTrackerEpic,
  createTrackerFormEpic,
  ctaSetTrackerEpic,
)
